<template>
  <div class="mt-4" style="padding-bottom: 96px;">
    <!-- Bypass confirmation modal — centered. Shown when the operator ticks
         the "Bypass SKU matching validation" checkbox. Cancel reverts the
         tick; Confirm keeps it enabled. Uses Bootstrap 4 modal API (jQuery)
         which is already wired up project-wide. -->
    <div
      id="tc-skip-prize-set-check-modal"
      ref="skipPrizeSetCheckModal"
      class="modal fade"
      tabindex="-1"
      role="dialog"
      aria-labelledby="tc-skip-prize-set-check-modal-title"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="tc-skip-prize-set-check-modal-title">
              <i class="ri-alert-line text-warning mr-2"></i>{{ t('features.input_packing.skip_prize_set_check') }}
            </h5>
          </div>
          <div class="modal-body" style="white-space: pre-line;">
            {{ t('features.input_packing.skip_prize_set_check_warning') }}
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="cancelSkipPrizeSetCheck">
              {{ t('actions.cancel') }}
            </button>
            <button type="button" class="btn btn-warning" @click="confirmSkipPrizeSetCheck">
              {{ t('actions.confirm') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Transient error toast — fixed top-right, auto-dismisses after 10s.
         Renders only when serverError is set. Click X for manual dismiss.
         z-index above sticky bar (1031) and below modals (1040). -->
    <transition name="tc-toast-fade">
      <div
        v-if="serverError"
        class="tc-toast alert alert-danger shadow"
        role="alert"
        @click="clearServerError"
      >
        <div class="d-flex align-items-start">
          <i class="ri-error-warning-line mr-2 mt-1"></i>
          <div class="flex-grow-1">{{ serverError }}</div>
          <button
            type="button"
            class="close ml-2"
            aria-label="Close"
            @click.stop="clearServerError"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      </div>
    </transition>

    <main>
      <div class="row">
        <div class="col-md-4" v-for="box in boxes" :key="box.id">
          <Box
            :box="box"
            @move-item="onMoveItem"
            @delete-box="onDeleteBox"
            @delete-item="onDeleteItem"
            @update-size="onUpdateBoxSize"
            @update-weight="onUpdateBoxWeight"
            @update-shipping-method="onUpdateBoxShippingMethod"
          />
        </div>
      </div>
    </main>

    <!-- Sticky action bar — single Back + Save row pinned to the viewport
         bottom so the operator never has to scroll to find Save. Uses
         Bootstrap's .fixed-bottom (not scoped CSS) so the rule applies
         even when the Webpacker HMR pipeline lags on style updates. The
         page reserves matching bottom padding (.tc-packing-page) so the
         last box is not hidden behind the bar. -->
    <!-- z-index sits one above Bootstrap's $zindex-fixed (1030) to keep the
         bar above sticky/affixed elements but below modals (1040). Native
         <select> widgets on Windows Chrome render outside the CSS stacking
         context, so when a long box list scrolls under the bar the carrier
         dropdown will still bleed through — acceptable for this internal
         admin flow. -->
    <div
      class="fixed-bottom bg-white border-top py-2"
      style="box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.05); z-index: 1031;"
    >
      <div class="container-fluid">
        <div class="row align-items-center justify-content-between no-gutters mx-2">
          <div class="col-auto">
            <button
              class="btn btn-outline-primary px-3"
              @click="onAddBox"
              :disabled="loading"
            >
              <i class="ri-add-line mr-1"></i>
              {{ t('features.input_packing.add_box') }}
            </button>
          </div>
          <div class="col-auto">
            <div class="row align-items-center">
              <div class="col-auto pr-2" v-if="country">
                <span class="badge badge-secondary font-size-md">
                  {{ t('features.input_packing.ship_to') }}: {{ country }}
                </span>
              </div>
              <div class="col-auto pr-2">
                <div class="custom-control custom-checkbox" :title="t('features.input_packing.skip_prize_set_check_hint')">
                  <input
                    id="tc-skip-prize-set-check"
                    type="checkbox"
                    class="custom-control-input"
                    :checked="skipPrizeSetCheck"
                    :disabled="loading"
                    @change="onCheckboxChange"
                  />
                  <label class="custom-control-label" for="tc-skip-prize-set-check">
                    {{ t('features.input_packing.skip_prize_set_check') }}
                  </label>
                </div>
              </div>
              <div class="col-auto">
                <button class="btn btn-info px-4" @click="backToList" :disabled="loading">
                  {{ t('actions.simple_back') }}
                </button>
              </div>
              <div class="col-auto">
                <button class="btn btn-primary px-4" @click="savePacking" :disabled="loading">
                  {{ loading ? t('actions.saving') : t('actions.save') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Box from './components/tokyo-catch/box.vue'
import { mapStores } from 'pinia'
import { useTokyoCatchInputPackingStore } from "./stores/tokyoCatchInputPackingStore"

export default {
  inject: ['i18n'],
  components: { Box },
  props: {
    initialData: { type: Object, required: true }
  },
  data() {
    return {
      t: (key, options) => this.i18n.t(key, options),
      loading: false,
      serverError: null,
      serverErrorTimer: null,
    }
  },
  watch: {
    serverError(val) {
      // Auto-dismiss the toast after 10s. Re-arm the timer whenever a new
      // error is set so consecutive failures each get their full window.
      if (this.serverErrorTimer) {
        clearTimeout(this.serverErrorTimer)
        this.serverErrorTimer = null
      }
      if (val) {
        this.serverErrorTimer = setTimeout(() => {
          this.serverError = null
          this.serverErrorTimer = null
        }, 10000)
      }
    }
  },
  computed: {
    ...mapStores(useTokyoCatchInputPackingStore),
    orderId() {
      return this.inputPackingStore.orderId
    },
    boxes() {
      return this.inputPackingStore.boxes
    },
    // Pull country from initialData so it is available immediately on render
    // regardless of whether fetchOrder() throws — keeping it out of data()
    // also prevents a stale local copy from drifting from the server payload.
    country() {
      return this.initialData && this.initialData.country ? this.initialData.country : null
    },
    skipPrizeSetCheck() {
      return this.inputPackingStore.skipPrizeSetCheck
    }
  },
  async mounted() {
    this.inputPackingStore.orderId = this.initialData.orderId
    this.inputPackingStore.shipmentReviewId = this.initialData.shipmentReviewId
    this.inputPackingStore.boxShippingOptions = this.initialData.boxShippingOptions || {}
    this.inputPackingStore.boxSizeOptions = this.initialData.boxSizeOptions || []
    this.loading = true
    try {
      await this.inputPackingStore.fetchOrder()
    } catch (e) {
      this.serverError = e.message
    } finally {
      this.loading = false
    }

    await this.$nextTick(function () {
      document
        .querySelectorAll('[data-toggle="tooltip"]')
        .forEach(el => $(el).tooltip())
    })

    // Needs to hide tooltips when it's dragged, otherwise the tooltip left on the screen
    document.addEventListener('dragstart', this.hideTooltips)
    document.addEventListener('dragend', this.hideTooltips)
  },
  beforeUnmount() {
    document.removeEventListener('dragstart', this.hideTooltips)
    document.removeEventListener('dragend', this.hideTooltips)
    if (this.serverErrorTimer) {
      clearTimeout(this.serverErrorTimer)
      this.serverErrorTimer = null
    }
  },
  methods: {
    onAddBox() {
      this.clearStaleServerError()
      this.inputPackingStore.addBox()
    },
    hideTooltips() {
      document
        .querySelectorAll('[data-toggle="tooltip"]')
        .forEach(el => {
          $(el).tooltip('hide')
        })

      document
        .querySelectorAll('.tooltip')
        .forEach(t => t.remove())
    },
    onMoveItem(payload) {
      this.clearStaleServerError()
      this.inputPackingStore.moveItem(payload)
    },
    onDeleteBox(boxId) {
      this.clearStaleServerError()
      this.inputPackingStore.removeBox(boxId)
    },
    onDeleteItem({ boxId, itemId }) {
      this.clearStaleServerError()
      this.inputPackingStore.removeItem({ boxId, itemId })
    },
    onUpdateBoxSize(payload) {
      this.clearStaleServerError()
      this.inputPackingStore.updateBoxSize(payload)
    },
    onUpdateBoxWeight(payload) {
      this.clearStaleServerError()
      this.inputPackingStore.updateBoxWeight(payload)
    },
    onUpdateBoxShippingMethod(payload) {
      this.clearStaleServerError()
      this.inputPackingStore.updateBoxShippingMethod(payload)
    },
    // Any edit to size, weight, carrier or prizes invalidates the last save's
    // error message — clear it so the operator isn't staring at stale copy.
    clearStaleServerError() {
      if (this.serverError) this.serverError = null
    },
    clearServerError() {
      this.serverError = null
    },
    onCheckboxChange(event) {
      this.clearStaleServerError()
      const wantsEnable = event.target.checked
      if (wantsEnable) {
        // Revert the native toggle and require explicit confirmation in the
        // modal before flipping the store value to true.
        event.target.checked = false
        $(this.$refs.skipPrizeSetCheckModal).modal({ backdrop: 'static', keyboard: false })
      } else {
        // Unticking is the safe direction — apply immediately, no confirmation.
        this.inputPackingStore.setSkipPrizeSetCheck(false)
      }
    },
    confirmSkipPrizeSetCheck() {
      this.inputPackingStore.setSkipPrizeSetCheck(true)
      $(this.$refs.skipPrizeSetCheckModal).modal('hide')
    },
    cancelSkipPrizeSetCheck() {
      this.inputPackingStore.setSkipPrizeSetCheck(false)
      $(this.$refs.skipPrizeSetCheckModal).modal('hide')
    },
    async savePacking() {
      const emptyIdx = this.boxes.findIndex(b => !b.wonPrizes || b.wonPrizes.length === 0)
      if (emptyIdx !== -1) {
        this.serverError = `Box-${emptyIdx + 1} must contain at least one prize before saving.`
        return
      }
      const badWeightIdx = this.boxes.findIndex(b => {
        const w = b.weight
        if (w === null || w === undefined || w === '') return false
        const n = Number(w)
        return Number.isNaN(n) || n <= 0
      })
      if (badWeightIdx !== -1) {
        this.serverError = `Box-${badWeightIdx + 1} weight must be greater than 0 g when provided.`
        return
      }
      this.loading = true
      try {
        await this.inputPackingStore.savePacking()
        window.location.href = `/tokyo_catch/order_shipment_reviews/${this.inputPackingStore.shipmentReviewId}`
      } catch (e) {
        this.serverError = e.message
      } finally {
        this.loading = false
      }
    },
    backToList() {
      window.location.href = `/tokyo_catch/order_shipment_reviews/${this.inputPackingStore.shipmentReviewId}`
    }
  }
}
</script>

<style scoped>
.tc-toast {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 1035;
  max-width: 480px;
  min-width: 320px;
  cursor: pointer;
}
.tc-toast-fade-enter-active,
.tc-toast-fade-leave-active {
  transition: opacity 200ms ease, transform 200ms ease;
}
.tc-toast-fade-enter-from,
.tc-toast-fade-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}
</style>
